// Time formatting function
import type { CustomTime } from '@/types/all'
import { TicketStatus } from '@/types/api'

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
// Truncate long text (e.g., airport names)
export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

// Output "HH:m:s"
export const pgTimeToString = (pgTime: CustomTime): Record<string, string> | Error => {
  // Make sure the time is valid
  if (!pgTime.Valid) {
    return new Error('Invalid time')
  }

  // Calculate hours, minutes, seconds from microseconds
  const totalSeconds = Math.floor(pgTime.Microseconds / 1000000)

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const milliseconds = Math.floor((pgTime.Microseconds % 1000000) / 1000)

  // Format with padding
  const padded = (num: number) => String(num).padStart(2, '0')

  // Return formatted time string
  return {
    hours: padded(hours),
    minutes: padded(minutes),
    seconds: padded(seconds),
    milliseconds: milliseconds ? '.' + String(milliseconds).padStart(3, '0') : '',
  }
}

export const pgTimeToIso = (pgTime: CustomTime): string | Error => {
  const time = pgTimeToString(pgTime)
  if (time instanceof Error) {
    return time
  }

  return `${time.hours}:${time.minutes}:${time.seconds}${time.milliseconds}`
}

// Format date strings
export const formatDateCompact = (dateString: string | null) => {
  if (!dateString) return 'N/A'

  // Split the date string directly instead of creating a Date object
  const [year, month, day] = dateString.split('-')

  // Array of month names
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  // Get the month name (subtract 1 as month is 0-indexed in the array)
  const monthName = monthNames[parseInt(month) - 1]

  // Remove leading zero from day if present
  const dayFormatted = parseInt(day).toString()

  return `${monthName} ${dayFormatted}`
}

// Formats a string of "10:55:52" format to "10:55 AM"
export const formatTimeStr = (time?: string): string => {
  if (!time) return ''

  // Split the time string by colon
  const [hoursStr, minutesStr] = time.split(':')

  // Parse hours and minutes as numbers
  const hours = parseInt(hoursStr, 10)
  const minutes = minutesStr ? parseInt(minutesStr, 10) : 0

  // Determine if it's AM or PM
  const period = hours >= 12 ? 'PM' : 'AM'

  // Convert to 12-hour format
  const displayHours = hours % 12 || 12 // If hours % 12 is 0, use 12 instead

  // Format minutes to ensure two digits
  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`

  return `${displayHours}:${formattedMinutes} ${period}`
}

export const formatTime = (time?: CustomTime): string => {
  if (!time || !time.Valid) return ''

  const constructedTime = pgTimeToString(time)
  if (constructedTime instanceof Error) {
    return ''
  }
  const toNumber = (str: string): number => parseInt(str, 10)
  // Format as 12-hour time with AM/PM
  const period = toNumber(constructedTime.hours) >= 12 ? 'PM' : 'AM'
  const displayHours = toNumber(constructedTime.hours) % 12 || 12

  return `${displayHours}:${constructedTime.minutes} ${period}`
}

// Date format to Day, Month Date
export const formatDate = (date?: string): string => {
  if (!date) return ''

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
  return new Date(date).toLocaleDateString('en-US', options)
}

export const convertMicroToMilliseconds = (microseconds: number): number => {
  return Math.floor(microseconds / 1000)
}

export const calculateFlightDuration = (
  startTime: string,
  startDate: string,
  depTimezone: string,
  depUtcOffset: number,
  endTime: string,
  endDate: string,
  arrTimezone: string,
  arrUtcOffset: number,
): string => {
  // Parse the start date (assuming format is "YYYY-MM-DD")
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number)

  // Parse the end date
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number)

  // Parse the time strings (format "HH:MM:SS")
  const [startHours, startMinutes, startSeconds] = startTime.split(':').map(Number)
  const [endHours, endMinutes, endSeconds] = endTime.split(':').map(Number)

  // Create Date objects for departure and arrival in their local time
  // Month is 0-indexed in JavaScript Date
  const departureLocalTime = new Date(
    startYear,
    startMonth - 1,
    startDay,
    startHours,
    startMinutes,
    startSeconds,
  )

  const arrivalLocalTime = new Date(endYear, endMonth - 1, endDay, endHours, endMinutes, endSeconds)

  // Convert to UTC by adjusting for the UTC offset
  // Local time - UTC offset = UTC time
  const departureTimeUTC = new Date(departureLocalTime.getTime() - depUtcOffset * 60 * 60 * 1000)
  const arrivalTimeUTC = new Date(arrivalLocalTime.getTime() - arrUtcOffset * 60 * 60 * 1000)

  // Calculate duration in milliseconds
  const durationMs = arrivalTimeUTC.getTime() - departureTimeUTC.getTime()

  // Handle negative duration (should not happen in practice)
  if (durationMs < 0) {
    console.error('Negative flight duration calculated', {
      departure: departureTimeUTC,
      arrival: arrivalTimeUTC,
    })
    return 'Invalid'
  }

  // Convert to hours and minutes
  const totalMinutes = Math.floor(durationMs / (1000 * 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  // Format the output
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  } else {
    return `${minutes}m`
  }
}

export const browserTimeNow = (): string => {
  const now = new Date()
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return now.toLocaleString('en-US', { timeZone: timezone, dateStyle: 'medium', timeStyle: 'full' })
}

// Define the type for timezone formatting parts
interface TimezoneParts {
  year?: string
  month?: string
  day?: string
  hour?: string
  minute?: string
  second?: string
  timeZoneName?: string
  [key: string]: string | undefined
}

/**
 * Calculates time until departure with proper timezone and DST handling
 * @param time Flight departure time in microseconds
 * @param date Flight departure date in YYYY-MM-DD format
 * @param timezone IANA timezone identifier (e.g., "America/New_York")
 * @param utcOffset UTC offset in hours (used as fallback)
 * @returns Formatted string indicating time until departure
 */
export const timeToDeparture = (
  time: string,
  date: string,
  timezone: string,
  utcOffset: number,
): string => {
  // Parse the date string (assuming format is "YYYY-MM-DD")
  const [year, month, day] = date.split('-').map((num) => parseInt(num, 10))

  // Parse the time string (assuming format is "HH:MM:SS")
  const [hours, minutes, seconds] = time.split(':').map((num) => parseInt(num, 10))

  let departureTimeUTC: Date

  try {
    // Method 1: Use Intl API to handle timezone conversion correctly (including DST)
    if (timezone && isValidTimeZone(timezone)) {
      // Format the date components for the specific timezone
      // We need to create a UTC string with the local time digits
      const utcString = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}Z`

      // Create a date object that represents the local time in UTC
      const utcDate = new Date(utcString)

      // Now determine the UTC time that corresponds to the local time in the target timezone
      // We'll use the Intl API to get the timezone-specific offset
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
        timeZoneName: 'short',
      })

      // Get formatted parts from the target timezone
      const parts = formatter.formatToParts(utcDate)

      // Extract components from timezone formatting
      const tzParts: TimezoneParts = {}
      parts.forEach((part) => {
        if (part.type !== 'literal') {
          tzParts[part.type] = part.value
        }
      })

      // Calculate the offset from UTC based on the timeZoneName
      const tzName = tzParts.timeZoneName || ''
      const dstActive = tzName.includes('D') // 'D' for Daylight in MDT, EDT, etc.

      // Calculate DST-aware UTC time by assuming tzHour is in the target timezone
      // and converting back to UTC using the real-time offset
      const targetDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds))

      // Directly use hourly offset as a fallback when Intl API calculations are problematic
      const adjustedTime = new Date(
        targetDate.getTime() - utcOffset * 3600 * 1000 - (dstActive ? 3600 * 1000 : 0), // Subtract an hour if DST is active
      )

      departureTimeUTC = adjustedTime
    } else {
      // Method 2: Fall back to the UTC offset approach
      console.log(`Falling back to UTC offset: ${utcOffset}`)

      // Calculate UTC hours by subtracting the UTC offset
      let utcHours = hours - utcOffset
      let utcDay = day
      let utcMonth = month
      let utcYear = year

      // Handle day boundary crossings
      if (utcHours < 0) {
        utcHours += 24
        // Create a new date for previous day
        const prevDay = new Date(Date.UTC(year, month - 1, day))
        prevDay.setUTCDate(prevDay.getUTCDate() - 1)
        utcDay = prevDay.getUTCDate()
        utcMonth = prevDay.getUTCMonth() + 1 // Month is 0-indexed
        utcYear = prevDay.getUTCFullYear()
      } else if (utcHours >= 24) {
        utcHours -= 24
        // Create a new date for next day
        const nextDay = new Date(Date.UTC(year, month - 1, day))
        nextDay.setUTCDate(nextDay.getUTCDate() + 1)
        utcDay = nextDay.getUTCDate()
        utcMonth = nextDay.getUTCMonth() + 1
        utcYear = nextDay.getUTCFullYear()
      }

      // Create UTC timestamp
      departureTimeUTC = new Date(
        Date.UTC(utcYear, utcMonth - 1, utcDay, utcHours, minutes, seconds),
      )
    }
  } catch (e) {
    // Method 3: Last resort fallback
    console.error('Error handling timezone, using simple offset conversion', e)
    departureTimeUTC = new Date(Date.UTC(year, month - 1, day, hours - utcOffset, minutes, seconds))
  }

  // Get current time in UTC
  const now = new Date()
  const nowUTC = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
  )

  // Calculate difference in milliseconds
  const timeDifference = departureTimeUTC.getTime() - nowUTC

  // Check if flight has already departed
  if (timeDifference <= 0) {
    return 'Departed'
  }

  // Convert to minutes, hours, and days
  const totalMinutes = Math.floor(timeDifference / (1000 * 60))
  const totalHours = Math.floor(totalMinutes / 60)
  const days = Math.floor(totalHours / 24)
  const remainingHours = totalHours % 24
  const remainingMinutes = totalMinutes % 60

  // Format the output
  if (days > 0) {
    return `Departing in ${days}d ${remainingHours}h`
  } else if (remainingHours > 0) {
    return `Departing in ${remainingHours}h ${remainingMinutes}m`
  } else {
    return `Departing in ${remainingMinutes}m`
  }
}

/**
 * Calculates time until arrival with proper timezone and DST handling
 * @param time Flight arrival time in microseconds
 * @param date Flight arrival date in YYYY-MM-DD format
 * @param timezone IANA timezone identifier (e.g., "America/New_York")
 * @param utcOffset UTC offset in hours (used as fallback)
 * @returns Formatted string indicating time until arrival
 */
export const timeToArrival = (
  time: string,
  date: string,
  timezone: string,
  utcOffset: number,
): string => {
  // Parse the date string (assuming format is "YYYY-MM-DD")
  const [year, month, day] = date.split('-').map((num) => parseInt(num, 10))

  // Parse the time string (assuming format is "HH:MM:SS")
  const [hours, minutes, seconds] = time.split(':').map((num) => parseInt(num, 10))

  let arrivalTimeUTC: Date

  try {
    // Method 1: Use Intl API to handle timezone conversion correctly (including DST)
    if (timezone && isValidTimeZone(timezone)) {
      // Format the date components for the specific timezone
      // We need to create a UTC string with the local time digits
      const utcString = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}Z`

      // Create a date object that represents the local time in UTC
      const utcDate = new Date(utcString)

      // Now determine the UTC time that corresponds to the local time in the target timezone
      // We'll use the Intl API to get the timezone-specific offset
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
        timeZoneName: 'short',
      })

      // Get formatted parts from the target timezone
      const parts = formatter.formatToParts(utcDate)

      // Extract components from timezone formatting
      const tzParts: TimezoneParts = {}
      parts.forEach((part) => {
        if (part.type !== 'literal') {
          tzParts[part.type] = part.value
        }
      })

      // Calculate the offset from UTC based on the timeZoneName
      const tzName = tzParts.timeZoneName || ''
      const dstActive = tzName.includes('D') // 'D' for Daylight in MDT, EDT, etc.

      // Calculate DST-aware UTC time by assuming tzHour is in the target timezone
      // and converting back to UTC using the real-time offset
      const targetDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds))

      // Directly use hourly offset as a fallback when Intl API calculations are problematic
      const adjustedTime = new Date(
        targetDate.getTime() - utcOffset * 3600 * 1000 - (dstActive ? 3600 * 1000 : 0), // Subtract an hour if DST is active
      )

      arrivalTimeUTC = adjustedTime
    } else {
      // Method 2: Fall back to the UTC offset approach
      console.log(`Falling back to UTC offset: ${utcOffset}`)

      // Calculate UTC hours by subtracting the UTC offset
      let utcHours = hours - utcOffset
      let utcDay = day
      let utcMonth = month
      let utcYear = year

      // Handle day boundary crossings
      if (utcHours < 0) {
        utcHours += 24
        // Create a new date for previous day
        const prevDay = new Date(Date.UTC(year, month - 1, day))
        prevDay.setUTCDate(prevDay.getUTCDate() - 1)
        utcDay = prevDay.getUTCDate()
        utcMonth = prevDay.getUTCMonth() + 1 // Month is 0-indexed
        utcYear = prevDay.getUTCFullYear()
      } else if (utcHours >= 24) {
        utcHours -= 24
        // Create a new date for next day
        const nextDay = new Date(Date.UTC(year, month - 1, day))
        nextDay.setUTCDate(nextDay.getUTCDate() + 1)
        utcDay = nextDay.getUTCDate()
        utcMonth = nextDay.getUTCMonth() + 1
        utcYear = nextDay.getUTCFullYear()
      }

      // Create UTC timestamp
      arrivalTimeUTC = new Date(Date.UTC(utcYear, utcMonth - 1, utcDay, utcHours, minutes, seconds))
    }
  } catch (e) {
    // Method 3: Last resort fallback
    console.error('Error handling timezone, using simple offset conversion', e)
    arrivalTimeUTC = new Date(Date.UTC(year, month - 1, day, hours - utcOffset, minutes, seconds))
  }

  // Get current time in UTC
  const now = new Date()
  const nowUTC = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
  )

  // Calculate difference in milliseconds
  const timeDifference = arrivalTimeUTC.getTime() - nowUTC

  // Check if flight has already arrived
  if (timeDifference <= 0) {
    return 'Arrived'
  }

  // Convert to minutes, hours, and days
  const totalMinutes = Math.floor(timeDifference / (1000 * 60))
  const totalHours = Math.floor(totalMinutes / 60)
  const days = Math.floor(totalHours / 24)
  const remainingHours = totalHours % 24
  const remainingMinutes = totalMinutes % 60

  // Format the output
  if (days > 0) {
    return `Arriving in ${days}d ${remainingHours}h`
  } else if (remainingHours > 0) {
    return `Arriving in ${remainingHours}h ${remainingMinutes}m`
  } else {
    return `Arriving in ${remainingMinutes}m`
  }
}

/**
 * Helper function to check if a timezone string is valid
 * @param tz Timezone string to validate
 * @returns boolean indicating if the timezone is valid
 */
function isValidTimeZone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch (e) {
    console.error(`Invalid timezone: ${tz}`, e)
    return false
  }
}
export const formatTicketStatus = (status: TicketStatus) => {
  if (!status) return 'N/A'
  switch (status) {
    case TicketStatus.Active:
      return 'Active'
    case TicketStatus.Open:
      return 'Open'
    case TicketStatus.Exchange:
      return 'Exchange'
    case TicketStatus.Refund:
      return 'Refund'
    case TicketStatus.Void:
      return 'Void'
    case TicketStatus.Used:
      return 'Used'
    default:
      return status
  }
}

// Get a slice of data based on the current page and page size
export const paginatedResults = <T>(currentPage: number, pageSize: number, data: T[]): T[] => {
  const start = (currentPage - 1) * pageSize
  const end = currentPage * pageSize
  return data.slice(start, end)
}

export const formatCurrency = (value: number | null) => {
  if (value === null) return 'N/A'
  if (!value && value !== 0) return 'N/A'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

export const capitalizeFirstChar = (str: string): string => {
  str = str.toLowerCase()
  const strArray = str.split(' ')

  const capitalizeFirstChar = strArray.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  return capitalizeFirstChar.join(' ')
}

// Function to get month name from month number(1: Jan, 2: Feb, ...)
export const getMonthName = (monthNum: number) => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  // Adjust for 0-based vs 1-based indexing
  if (monthNum >= 1 && monthNum <= 12) {
    return monthNames[monthNum - 1]
  }

  return String(monthNum) // Fallback
}

// Returns an array of 1-12 for months
export const getMonthsArray = (): number[] => {
  return Array.from({ length: 12 }, (_, i) => i + 1)
}
